import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.1, ease: "easeOut" },
  }),
};

export default function PricingSection({ data, registerSection }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) registerSection("pricing", ref.current);
  }, [registerSection]);

  if (!data) return null;

  const tierCount = data.tiers?.length || 0;
  // Adaptive grid: 2 cols for 4 tiers on md, 4 cols on xl
  const gridCols =
    tierCount === 4
      ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-4"
      : tierCount === 3
      ? "grid-cols-1 md:grid-cols-3"
      : "grid-cols-1 md:grid-cols-2";

  return (
    <section
      ref={ref}
      id="pricing"
      className="py-16 md:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center max-w-2xl mx-auto mb-12 md:mb-16"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
            Investment
          </p>
          <h2 className="font-heading text-2xl md:text-3xl tracking-[-0.01em] text-foreground">
            {data.section_title}
          </h2>
          <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
            {data.section_subtitle}
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className={`grid ${gridCols} gap-4 md:gap-5 items-start`}>
          {data.tiers?.map((tier, i) => (
            <motion.div
              key={tier.name}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              custom={i}
            >
              <Card
                data-testid="pricing-tier-card"
                className={`rounded-2xl border p-6 md:p-7 flex flex-col h-full card-hover ${
                  tier.recommended
                    ? "border-[hsl(var(--ring))] shadow-[var(--shadow-crisp)] relative"
                    : "border-border shadow-[var(--shadow-soft)] bg-card"
                }`}
              >
                {tier.recommended && (
                  <Badge className="absolute -top-3 left-6 bg-[hsl(var(--brand-brass))] text-white text-[10px] uppercase tracking-[0.12em] font-mono px-3 py-1 rounded-full hover:bg-[hsl(var(--brand-brass))]">
                    Most Popular
                  </Badge>
                )}

                <div className="mb-5">
                  <h3 className="font-heading text-lg xl:text-xl text-foreground leading-tight">
                    {tier.name}
                  </h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-heading text-3xl md:text-3xl text-foreground">
                      {tier.price}
                    </span>
                    {tier.period && (
                      <span className="text-sm text-muted-foreground">
                        {tier.period}
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {tier.description}
                  </p>
                </div>

                <Separator className="mb-5" />

                <ul className="space-y-2.5 mb-6 flex-grow">
                  {tier.features?.map((feature, fi) => (
                    <li
                      key={fi}
                      className="flex items-start gap-2.5 text-sm text-foreground"
                    >
                      <Check className="h-4 w-4 text-[hsl(var(--brand-brass))] flex-shrink-0 mt-0.5" />
                      <span className="leading-snug">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid="pricing-select-plan-button"
                  className={`w-full h-11 text-sm rounded-xl btn-primary-hover ${
                    tier.recommended
                      ? "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
                      : "bg-background text-foreground border border-border hover:border-[hsl(var(--ring))] hover:bg-accent"
                  }`}
                  variant={tier.recommended ? "default" : "outline"}
                >
                  {tier.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
